import { Download, Star, HeartHandshake, Quote } from "lucide-react";
import { APP_STORE_URL } from "@/constants";
import { AppStoreButton } from "@/components/AppStoreButton";

const WRITE_REVIEW_URL = `${APP_STORE_URL}?action=write-review`;

const steps = [
  {
    id: "rating-step-1",
    num: "01",
    title: "Get the app from the App Store",
    description:
      "Tap the download button below or search “Payment Pulse” by Walter Paul Bebirian on your iPhone or iPad.",
    Icon: Download,
    link: APP_STORE_URL,
  },
  {
    id: "rating-step-2",
    num: "02",
    title: "Tap here to open the review form",
    description:
      "This link jumps straight to Payment Pulse's “Write a Review” screen on your iPhone — no scrolling or searching needed.",
    Icon: Star,
    link: WRITE_REVIEW_URL,
  },
  {
    id: "rating-step-3",
    num: "03",
    title: "Tap 5 stars & share your routine",
    description:
      "Select all 5 stars, write one sentence about how knowing your safe-to-spend cash helps you, and tap Submit.",
    Icon: HeartHandshake,
    link: null,
  },
];

export const RatingGuideSection = () => (
  <section
    id="rate"
    data-testid="rating-guide-section"
    className="mx-auto max-w-6xl px-4 py-20 sm:px-6 lg:px-8"
  >
    <p className="font-mono-num text-xs font-semibold uppercase tracking-[0.2em] text-sky-400">
      Grow together with Walter Paul Bebirian
    </p>
    <h2 className="mt-3 max-w-2xl text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">
      Leave your 5-star review in 3 quick steps
    </h2>
    <p className="mt-4 max-w-2xl text-base text-slate-400">
      Your feedback shapes every release and helps more independent earners
      discover financial control.
    </p>

    <ol className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-3">
      {steps.map(({ id, num, title, description, Icon, link }) => {
        const inner = (
          <>
            <div className="flex items-center justify-between">
              <span className="font-mono-num text-3xl font-semibold text-sky-400/50">
                {num}
              </span>
              <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-sky-400/25 bg-sky-400/10">
                <Icon className="h-5 w-5 text-sky-400" />
              </span>
            </div>
            <h3 className="mt-5 text-lg font-semibold tracking-tight text-white">
              {title}
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-slate-400">
              {description}
            </p>
            {link === WRITE_REVIEW_URL && (
              <span className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-amber-400/30 bg-amber-400/10 px-3 py-1 text-[11px] font-medium text-amber-300">
                Opens the rating form directly
              </span>
            )}
          </>
        );
        return (
          <li key={id} data-testid={id}>
            {link ? (
              <a
                href={link}
                target="_blank"
                rel="noopener noreferrer"
                className="block h-full rounded-2xl border border-white/10 bg-[#0D1527]/90 p-7 transition-[border-color,transform] duration-300 hover:-translate-y-1 hover:border-sky-400/40"
              >
                {inner}
              </a>
            ) : (
              <div className="h-full rounded-2xl border border-white/10 bg-[#0D1527]/90 p-7">
                {inner}
              </div>
            )}
          </li>
        );
      })}
    </ol>

    <div
      data-testid="rating-creator-note"
      className="mt-10 flex flex-col gap-4 rounded-2xl border border-white/10 bg-white/[0.03] p-8 sm:flex-row sm:items-start"
    >
      <Quote className="h-6 w-6 shrink-0 text-sky-400" />
      <div>
        <p className="text-base leading-relaxed text-slate-200">
          “Every single 5-star review keeps Payment Pulse independent and
          focused purely on user utility rather than ad monetization.”
        </p>
        <p className="mt-3 text-sm font-medium text-slate-500">
          — Walter Paul Bebirian, creator of Payment Pulse
        </p>
      </div>
    </div>

    <div className="mt-10 flex justify-center">
      <AppStoreButton testId="rating-appstore-cta" sublabel="Step 1 starts here" />
    </div>
  </section>
);
